// Angular
import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FilteredViewService } from '../../../../../core/services/filtered-view.service';
import { select, Store } from '@ngrx/store';
import { currentUser } from '../../../../../core/auth';
import { forkJoin, fromEvent, of, Subscription } from 'rxjs';
import { AppState } from '../../../../../core/reducers';
import { PageEvent } from '@angular/material/paginator';
import { UserService } from '../../../../../core/auth/_services';
import { debounceTime, map, tap } from 'rxjs/operators';
import {
  ContactsService,
  DoctorService,
  PatientsService,
} from '../../../../../core/lists/_services';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import { TaskService } from '../../../../../core/timeline/_services';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'kt-view-recent-accessed-dialog',
  templateUrl: './view-recent-accessed-dialog.component.html',
})
export class ViewRecentAccessedDialogComponent implements OnInit {
  // Public properties
  loading = false;
  onDeleteClick;
  subscriptions: Subscription[] = [];
  currentUser: any;
  currentSearch: any;
  list = [];
  page = 0;
  totalNumber = 0;
  size = 10;
  userOptions = [];
  allContacts = [];
  allEntities = [];
  searchResultOptions = [];
  @ViewChild('userEle', { static: false }) userEle: ElementRef;
  @ViewChild('searchEle', { static: true }) searchEle: ElementRef;

  constructor(
    private sessionService: FilteredViewService,
    private store: Store<AppState>,
    private patientsService: PatientsService,
    public stringUtils: StringUtilsService,
    private doctorService: DoctorService,
    private contactsService: ContactsService,
    private taskService: TaskService,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public dialogRef: MatDialogRef<ViewRecentAccessedDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.searchResult('');
  }

  ngOnInit() {
    setTimeout(() => {
      this.subscriptions.push(
        this.store.pipe(select(currentUser)).subscribe((res) => {
          if (res) {
            this.currentUser = res;
            if (this.userEle !== undefined) {
              this.userEle.nativeElement.value = res ? res.userName : '';
            }
          }
        }),
      );
      if (this.userEle !== undefined) {
        fromEvent(this.userEle.nativeElement, 'keyup')
          .pipe(
            debounceTime(600),
            tap((name) => {
              if (name) {
                this.userAutoComplete(this.userEle.nativeElement.value);
              }
            }),
          )
          .subscribe();
      }
      fromEvent(this.searchEle.nativeElement, 'keyup')
        .pipe(
          debounceTime(600),
          tap((name) => {
            if (name) {
              this.searchResult(this.searchEle.nativeElement.value);
            }
          }),
        )
        .subscribe();
      this.loadRecord();
    });
  }

  loadRecord() {
    this.loading = true;
    this.sessionService
      .getRecentSessions(this.currentUser, this.page, this.size, this.currentSearch)
      .subscribe((res) => {
        this.list = res.data;
        const entitiesId = this.list
          .map((e) => {
            if (e.entity_type !== 'CONTACT') return e.entity_id;
          })
          .filter((element) => {
            return element !== null && element !== undefined;
          });

        const contactsId = this.list
          .map((e) => {
            if (e.entity_type === 'CONTACT') return e.entity_id;
          })
          .filter((element) => {
            return element !== null && element !== undefined;
          });

        this.contactsService.getContactsByIds(contactsId).subscribe((res1) => {
          if (!res || res.data.length === 0) return of({});
          this.allContacts = res1.data.reduce((out, current) => {
            return { ...out, [current.id]: current };
          }, {});
          this.cdr.detectChanges();
        });
        this.taskService.getEntities(entitiesId).subscribe((result) => {
          if (!result || result.data.length === 0) return of({});
          this.allEntities = result.data.reduce((out, current) => {
            return { ...out, [current.id]: current };
          }, {});
          this.cdr.detectChanges();
        });
        this.totalNumber = res.count;
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  onNoClick(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.dialogRef.close();
  }

  onYesClick(): void {
    this.dialogRef.close(true); // Keep only this row
  }

  loadPage($event: PageEvent) {
    this.page = $event.pageIndex;
    this.size = $event.pageSize;
    this.loadRecord();
  }

  selectedUser(value: any) {
    this.currentUser = value;
    this.userEle.nativeElement.value = value ? value.name : '';
    this.loadRecord();
  }

  private userAutoComplete(term: string) {
    this.userService.searchUsers(term).subscribe((res) => {
      if (!res) {
        this.userOptions = [];
        return;
      }
      this.userOptions = res.data;
      this.cdr.detectChanges();
    });
  }

  selectedSearch(value: any) {
    this.currentSearch = value;
    this.searchEle.nativeElement.value = value ? this.stringUtils.displayName(value) : '';
    this.loadRecord();
  }

  searchResult(term) {
    forkJoin({
      patients: this.patientsService.searchPatients(term),
      doctors: this.doctorService.searchDoctors(term),
      contacts: this.contactsService.searchContacts(term),
    })
      .pipe(
        map((response) => {
          const patients = response.patients.data as Array<any>;
          const doctors = response.doctors.data as Array<any>;
          const contacts = response.contacts.data as Array<any>;
          return contacts.concat(patients, doctors);
        }),
      )
      .subscribe((data) => {
        if (!data) {
          this.searchResultOptions = [];
          return;
        }
        this.searchResultOptions = data;
        this.cdr.detectChanges();
      });
  }

  openRelatedTo(type, id) {
    this.dialogRef.close();
    switch (type) {
      case 'DOCTOR':
        this.router.navigate(['lists/doctors/edit/' + id]);
        return;
      case 'PATIENT':
        this.router.navigate(['lists/patients/edit', id]);
        return;
      case 'CONTACT':
        this.router.navigate(['lists/contacts/edit/' + id]);
        return;
    }
  }
}
